import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { AxiosProgressEvent } from 'axios';
import { ChatEvent, ChatMessage, ChatPendingConfirmation } from '../models/models';
import api from './api';

/**
 * Assistant IA intégré (service agence_chat, sans état).
 *
 * La conversation n'est stockée NULLE PART côté serveur : l'historique complet
 * vit ici (et en localStorage, par utilisateur — cf. AssistantDrawer) et est
 * renvoyé à chaque question. Le serveur répond par un flux SSE d'événements
 * (texte en direct, outils, messages, confirmation d'écriture).
 */

// En prod : même domaine, routé par nginx (/assistant/ → agence_chat).
// En local : REACT_APP_CHAT_URL=http://localhost:3040 dans .env.
const CHAT_URL = process.env.REACT_APP_CHAT_URL || '/assistant';

// ── State ────────────────────────────────────────────────────────────────────

interface AssistantState {
  open: boolean;
  messages: ChatMessage[];
  /** Texte de la réponse en cours d'écriture (avant réception du message complet). */
  streamingText: string;
  /** Outils en cours d'exécution. */
  activeTools: { id: string; name: string }[];
  /** Écritures proposées par l'assistant, en attente d'un clic Confirmer / Annuler. */
  pending: ChatPendingConfirmation[] | null;
  loading: boolean;
  error: string | null;
}

const initialState: AssistantState = {
  open: false,
  messages: [],
  streamingText: '',
  activeTools: [],
  pending: null,
  loading: false,
  error: null,
};

// ── Flux SSE via l'instance api (JWT + département injectés par l'intercepteur) ──

/**
 * POST en flux : l'adaptateur XHR expose le texte reçu au fil de l'eau
 * (onDownloadProgress). On découpe les événements `data: …\n\n` au fur et à
 * mesure, puis on traite le reliquat à la fin de la réponse.
 */
async function streamChat(
  path: string,
  body: Record<string, unknown>,
  onEvent: (e: ChatEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  let consumed = 0;

  const consume = (fullText: string, final: boolean) => {
    const pendingText = fullText.slice(consumed);
    const blocks = pendingText.split('\n\n');
    const complete = final ? blocks : blocks.slice(0, -1);
    for (const block of complete) {
      consumed += block.length + 2;
      const line = block.trim();
      if (!line.startsWith('data:')) continue;
      try {
        onEvent(JSON.parse(line.slice(5).trim()) as ChatEvent);
      } catch {
        /* bloc incomplet ou illisible : ignoré */
      }
    }
  };

  const response = await api.post(path, body, {
    baseURL: CHAT_URL,
    adapter: 'xhr',
    responseType: 'text',
    signal,
    onDownloadProgress: (e: AxiosProgressEvent) => {
      const text = (e.event?.target as XMLHttpRequest | undefined)?.responseText;
      if (typeof text === 'string') consume(text, false);
    },
  });
  if (typeof response.data === 'string') consume(response.data, true);
}

function errorMessage(error: any): string {
  if (error?.name === 'CanceledError') return '';
  return error?.response?.data?.message || "L'assistant est injoignable pour le moment.";
}

// ── Thunks ───────────────────────────────────────────────────────────────────

/** Nouvelle question. Le message utilisateur est ajouté par le reducer `pending`. */
export const sendAssistantMessage = createAsyncThunk<
  void,
  { text: string; departement_nom?: string },
  { rejectValue: string }
>('assistant/send', async ({ departement_nom }, thunkAPI) => {
  const { AssistantSlice } = thunkAPI.getState() as { AssistantSlice: AssistantState };
  try {
    await streamChat(
      '/chat',
      { messages: AssistantSlice.messages, departement_nom },
      (e) => thunkAPI.dispatch(assistantEventReceived(e)),
      thunkAPI.signal,
    );
  } catch (error: any) {
    return thunkAPI.rejectWithValue(errorMessage(error));
  }
});

/** Décision sur les écritures en attente (le `pending` signé est renvoyé tel quel). */
export const confirmAssistantAction = createAsyncThunk<
  void,
  { approved: boolean; pending: ChatPendingConfirmation[]; departement_nom?: string },
  { rejectValue: string }
>('assistant/confirm', async ({ approved, pending, departement_nom }, thunkAPI) => {
  const { AssistantSlice } = thunkAPI.getState() as { AssistantSlice: AssistantState };
  try {
    await streamChat(
      '/chat/confirm',
      { messages: AssistantSlice.messages, approved, pending, departement_nom },
      (e) => thunkAPI.dispatch(assistantEventReceived(e)),
      thunkAPI.signal,
    );
  } catch (error: any) {
    return thunkAPI.rejectWithValue(errorMessage(error));
  }
});

// ── Slice ────────────────────────────────────────────────────────────────────

const startRequest = (state: AssistantState) => {
  state.loading = true;
  state.error = null;
  state.streamingText = '';
  state.activeTools = [];
};

const endRequest = (state: AssistantState) => {
  state.loading = false;
  state.streamingText = '';
  state.activeTools = [];
};

const AssistantSlice = createSlice({
  name: 'assistant',
  initialState,
  reducers: {
    setAssistantOpen(state, action: PayloadAction<boolean>) {
      state.open = action.payload;
    },
    /** Restauration depuis le localStorage (au montage du drawer). */
    conversationLoaded(state, action: PayloadAction<{ messages: ChatMessage[]; pending: ChatPendingConfirmation[] | null }>) {
      state.messages = action.payload.messages;
      state.pending = action.payload.pending;
    },
    newConversation(state) {
      state.messages = [];
      state.pending = null;
      state.error = null;
      state.streamingText = '';
    },
    clearAssistantError(state) {
      state.error = null;
    },
    assistantEventReceived(state, action: PayloadAction<ChatEvent>) {
      const e = action.payload;
      switch (e.type) {
        case 'text':
          state.streamingText += e.delta;
          break;
        case 'tool_start':
          state.activeTools.push({ id: e.id, name: e.name });
          break;
        case 'tool_end':
          state.activeTools = state.activeTools.filter((t) => t.id !== e.id);
          break;
        case 'message':
          state.messages.push(e.message);
          if (e.message.role === 'assistant') state.streamingText = '';
          break;
        case 'confirm_required':
          state.pending = e.calls;
          break;
        case 'error':
          state.error = e.message;
          break;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendAssistantMessage.pending, (state, action) => {
        // Poser une nouvelle question abandonne une éventuelle confirmation en attente
        // (le serveur marque l'action « non confirmée »).
        state.pending = null;
        state.messages.push({ role: 'user', text: action.meta.arg.text });
        startRequest(state);
      })
      .addCase(sendAssistantMessage.fulfilled, endRequest)
      .addCase(sendAssistantMessage.rejected, (state, action) => {
        endRequest(state);
        if (action.payload) state.error = action.payload;
      })
      .addCase(confirmAssistantAction.pending, (state) => {
        state.pending = null;
        startRequest(state);
      })
      .addCase(confirmAssistantAction.fulfilled, endRequest)
      .addCase(confirmAssistantAction.rejected, (state, action) => {
        endRequest(state);
        if (action.payload) state.error = action.payload;
      });
  },
});

export const {
  setAssistantOpen,
  conversationLoaded,
  newConversation,
  clearAssistantError,
  assistantEventReceived,
} = AssistantSlice.actions;

export default AssistantSlice.reducer;
